import { CandleType, TooltipShowRule } from 'klinecharts'
import type {
  DeepPartial,
  IndicatorCreateTooltipDataSourceCallback,
  Styles,
} from 'klinecharts'
import type { ChartSettings } from './types'

/**
 * Native indicator legends are suppressed for ALL indicators — legends render as
 * HTML (status line + pane controls) so name/params/value/colour can be styled
 * independently of the canvas.
 */
export const EMPTY_TOOLTIP: IndicatorCreateTooltipDataSourceCallback = () => ({
  name: '',
  calcParamsText: '',
  icons: [],
  values: [],
})

/**
 * Full chart palette (ported from the prototype). `s` = the user's chart-settings
 * overrides; each key falls back to the theme default. `engineType` is the
 * klinecharts candle type for the active chart shape.
 */
export function buildStyles(
  isDark: boolean,
  s: ChartSettings = {},
  engineType: CandleType = CandleType.CandleSolid,
): DeepPartial<Styles> {
  const T = 'rgba(0,0,0,0)' // unchecked element → transparent (TV's checkbox-off)
  // Canvas legends/axes must name a real font — "inherit" is invalid on a canvas
  // and falls back to a different face. Inter matches the rest of the app.
  const FONT = 'Inter, -apple-system, system-ui, sans-serif'
  const textColor = s.text || (isDark ? '#888888' : '#737373')
  const gridDefault = isDark ? '#2e2e2e' : 'rgba(0,0,0,0.06)'
  const gridV = s.gridVert || gridDefault
  const gridH = s.gridHoriz || gridDefault
  const axisColor = s.scale || (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)')
  const up = s.candleUp || (isDark ? 'rgba(38,166,154,0.85)' : 'rgba(22,163,74,0.85)')
  const down = s.candleDown || (isDark ? 'rgba(242,54,69,0.85)' : 'rgba(220,38,38,0.85)')
  const upBorder = s.candleUpBorder || up
  const downBorder = s.candleDownBorder || down
  const wickUp = s.candleUpWick || (isDark ? 'rgba(38,166,154,0.6)' : 'rgba(22,163,74,0.6)')
  const wickDown = s.candleDownWick || (isDark ? 'rgba(242,54,69,0.6)' : 'rgba(220,38,38,0.6)')
  // checkbox toggles: off → render that part transparent (body=hollow, no border, no wick)
  const bodyUp = s.bodyOn === false ? T : up
  const bodyDown = s.bodyOn === false ? T : down
  const bordUp = s.bordersOn === false ? T : upBorder
  const bordDown = s.bordersOn === false ? T : downBorder
  const wUp = s.wickOn === false ? T : wickUp
  const wDown = s.wickOn === false ? T : wickDown
  const crossColor = s.crosshair || textColor
  const tickSize = s.textSize || 12
  const markBg = isDark ? '#2a2a2a' : '#f0f0f0'
  const markText = isDark ? '#e4e4e4' : '#1a1a1a'
  const lineColor = s.candleUp || (isDark ? '#2970ff' : '#2563eb') // Line/area shape colour

  return {
    grid: { horizontal: { color: gridH }, vertical: { color: gridV } },
    candle: {
      type: engineType,
      bar: {
        upColor: bodyUp,
        downColor: bodyDown,
        noChangeColor: textColor,
        upBorderColor: bordUp,
        downBorderColor: bordDown,
        upWickColor: wUp,
        downWickColor: wDown,
      },
      area: {
        lineColor,
        lineSize: 1.5,
        value: 'close',
        backgroundColor: [
          { offset: 0, color: isDark ? 'rgba(41,112,255,0.12)' : 'rgba(37,99,235,0.10)' },
          { offset: 1, color: isDark ? 'rgba(41,112,255,0)' : 'rgba(37,99,235,0)' },
        ],
      },
      // Keep the last-price line, in the last candle's colour; hide the high/low price-mark arrows.
      priceMark: {
        high: { show: false },
        low: { show: false },
        last: { upColor: up, downColor: down, noChangeColor: textColor, text: { color: markText } },
      },
      // Default OHLC tooltip is hidden — our own HTML status line renders it.
      tooltip: { showRule: TooltipShowRule.None, text: { color: textColor } },
    },
    indicator: {
      tooltip: {
        showRule: TooltipShowRule.Always,
        showName: true,
        showParams: true,
        offsetLeft: 4,
        offsetTop: 4,
        offsetRight: 4,
        offsetBottom: 4,
        text: {
          color: textColor,
          size: 12,
          family: FONT,
          weight: 'normal',
          marginLeft: 0,
          marginTop: 4,
          marginRight: 6,
          marginBottom: 0,
        },
      },
    },
    // Drawing anchor handles: outline-only (hollow) instead of a filled dot.
    overlay: {
      point: {
        color: isDark ? '#1a1a1a' : '#ffffff',
        borderColor: isDark ? '#e4e4e4' : '#1a1a1a',
        borderSize: 1,
        radius: 4,
        activeColor: isDark ? '#1a1a1a' : '#ffffff',
        activeBorderColor: '#2970ff',
        activeBorderSize: 1,
        activeRadius: 5,
      },
    },
    xAxis: {
      axisLine: { color: axisColor },
      tickLine: { color: axisColor },
      tickText: { color: textColor, size: Math.max(9, tickSize - 1), family: FONT },
    },
    yAxis: {
      axisLine: { color: axisColor },
      tickLine: { color: axisColor },
      tickText: { color: textColor, size: tickSize, family: FONT },
    },
    crosshair: {
      horizontal: {
        line: { color: crossColor },
        text: { backgroundColor: markBg, color: markText, borderColor: axisColor },
      },
      vertical: {
        line: { color: crossColor },
        text: { backgroundColor: markBg, color: markText, borderColor: axisColor },
      },
    },
  }
}

/** Chart wrapper background when no user override is set. */
export function defaultBackground(isDark: boolean): string {
  return isDark ? '#1a1a1a' : '#ffffff'
}
